import { PageHeader, DataTable, LoadingState, EmptyState, ActionToolbar, FilterBar } from '../components/AdminPageUI';
import SummaryStrip, { SummaryMetric } from '../components/SummaryStrip';
import React, { useState, useEffect, useCallback, useMemo, memo } from 'react';
import {
  Typography, Box, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, MenuItem, Paper, Table, TableHead, TableRow, TableCell, TableBody,
  IconButton, Select, FormControl, InputLabel, Card, CardContent,
  Avatar, Stack, Tooltip, Chip, Container, Grid, CircularProgress,
  Collapse, Alert, Divider, useMediaQuery
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Assignment as AssignmentIcon,
  Add as AddIcon,
  ExpandMore,
  ExpandLess,
  School,
  CalendarToday,
  Class,
  Visibility
} from '@mui/icons-material';
import { alpha, useTheme } from '@mui/material/styles';
import api from '../utils/Api';
import { format, startOfWeek } from 'date-fns';

// ✅ Helper function for consistent class name display
const getClassLabel = (cls) => cls?.displayName || cls?.name || '';

// ✅ Memoized Mobile Assignment Card Component
const MobileAssignmentCard = memo(({ assignment, onEdit, onDelete, theme }) => {
  const handleEdit = useCallback(() => onEdit(assignment), [assignment, onEdit]);
  const handleDelete = useCallback(() => onDelete(assignment), [assignment, onDelete]);

  return (
    <Card sx={{ mb: 2, boxShadow: 2, border: `1px solid ${theme.palette.divider}` }}>
      <CardContent sx={{ p: 2 }}>
        <Stack spacing={2}>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
            <Typography variant="h6" fontWeight="600" sx={{ flex: 1, mr: 1 }}>
              {assignment.title}
            </Typography>
            <Stack direction="row" spacing={0.5}>
              <Tooltip title="Edit Assignment">
                <IconButton 
                  onClick={handleEdit} 
                  color="primary" 
                  size="small"
                  sx={{
                    minWidth: 32,
                    minHeight: 32,
                    borderRadius: 1,
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.primary.main, 0.1),
                    },
                  }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete Assignment">
                <IconButton 
                  onClick={handleDelete} 
                  color="error" 
                  size="small"
                  sx={{
                    minWidth: 32,
                    minHeight: 32,
                    borderRadius: 1,
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.error.main, 0.1),
                    },
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          </Stack>

          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Chip 
              label={getClassLabel(assignment.class)} 
              size="small" 
              color="primary"
              variant="outlined"
            />
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <CalendarToday fontSize="small" color="action" />
              <Typography variant="body2" fontWeight="500">
                {new Date(assignment.dueDate).toLocaleDateString()}
              </Typography>
              {new Date(assignment.dueDate) < new Date() && (
                <Chip 
                  label="Overdue" 
                  size="small" 
                  color="error"
                  sx={{ ml: 1 }}
                />
              )}
            </Stack>
          </Stack>

          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ 
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden'
            }}>
              {assignment.description || 'No description provided'}
            </Typography>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
});

// ✅ Memoized Table Row Component
const AssignmentTableRow = memo(({ assignment, onEdit, onDelete, isTablet, theme }) => {
  const handleEdit = useCallback(() => onEdit(assignment), [assignment, onEdit]);
  const handleDelete = useCallback(() => onDelete(assignment), [assignment, onDelete]);

  return (
    <TableRow 
      key={assignment._id}
      sx={{ 
        '&:hover': { 
          bgcolor: alpha(theme.palette.primary.main, 0.02) 
        } 
      }}
    >
      <TableCell sx={{ py: 2, maxWidth: isTablet ? 200 : 300 }}>
        <Typography variant="subtitle1" fontWeight="500" noWrap>
          {assignment.title}
        </Typography>
      </TableCell>
      <TableCell sx={{ py: 2 }}>
        <Chip 
          label={getClassLabel(assignment.class)} 
          size="small" 
          color="primary"
          variant="outlined"
        />
      </TableCell>
      <TableCell sx={{ py: 2 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <CalendarToday fontSize="small" color="action" />
          <Typography variant="body2">
            {new Date(assignment.dueDate).toLocaleDateString()}
          </Typography>
          {new Date(assignment.dueDate) < new Date() && (
            <Chip 
              label="Overdue" 
              size="small" 
              color="error"
            />
          )}
        </Stack>
      </TableCell>
      <TableCell sx={{ py: 2, maxWidth: isTablet ? 200 : 300 }}>
        <Typography 
          variant="body2" 
          color="text.secondary"
          sx={{
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden'
          }}
        >
          {assignment.description || 'No description provided'}
        </Typography>
      </TableCell>
      <TableCell sx={{ py: 2, textAlign: 'center' }}>
        <Stack direction="row" spacing={1} justifyContent="center">
          <Tooltip title="Edit Assignment">
            <IconButton 
              onClick={handleEdit} 
              color="primary" 
              size="small"
              sx={{
                minWidth: 32,
                minHeight: 32,
                borderRadius: 1,
                '&:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.1),
                },
              }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete Assignment">
            <IconButton 
              onClick={handleDelete} 
              color="error" 
              size="small"
              sx={{
                minWidth: 32,
                minHeight: 32,
                borderRadius: 1,
                '&:hover': {
                  backgroundColor: alpha(theme.palette.error.main, 0.1),
                },
              }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      </TableCell>
    </TableRow>
  );
});

// ✅ Memoized Week Group Component
const WeekGroup = memo(({ week, assignments, isMobile, isTablet, onEdit, onDelete, theme }) => {
  const weekFormatted = useMemo(() => 
    format(new Date(week), 'MMMM d, yyyy'), [week]
  );

  return (
    <Box key={week} sx={{ mb: isMobile ? 3 : 4 }}>
      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent sx={{ 
          py: isMobile ? 1 : 2, 
          bgcolor: alpha(theme.palette.primary.main, 0.04) 
        }}>
          <Typography 
            variant={isMobile ? "subtitle1" : "h6"} 
            fontWeight="600" 
            color="primary"
          >
            📅 Week of {weekFormatted}
          </Typography>
        </CardContent>
      </Card>
      
      {isMobile ? (
        <Stack spacing={1}>
          {assignments.map((assignment) => (
            <MobileAssignmentCard 
              key={assignment._id} 
              assignment={assignment}
              onEdit={onEdit}
              onDelete={onDelete}
              theme={theme}
            />
          ))}
        </Stack>
      ) : (
        <DataTable><Table sx={{ minWidth: isTablet ? 800 : 'auto' }}>
          <TableHead sx={{ bgcolor: theme.palette.background.default }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold', py: 2 }}>TITLE</TableCell>
              <TableCell sx={{ fontWeight: 'bold', py: 2 }}>CLASS</TableCell>
              <TableCell sx={{ fontWeight: 'bold', py: 2 }}>DUE DATE</TableCell>
              <TableCell sx={{ fontWeight: 'bold', py: 2 }}>DESCRIPTION</TableCell>
              <TableCell sx={{ fontWeight: 'bold', py: 2, textAlign: 'center' }}>ACTIONS</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
{assignments.length === 0 && <TableRow><TableCell colSpan={100}><EmptyState title="No records found" description="Records will appear here when available. Check your selected filters." /></TableCell></TableRow>}

            {assignments.map((assignment) => (
              <AssignmentTableRow
                key={assignment._id}
                assignment={assignment}
                onEdit={onEdit}
                onDelete={onDelete}
                isTablet={isTablet}
                theme={theme}
              />
            ))}
          </TableBody>
        </Table></DataTable>
      )}
    </Box>
  );
});

// ✅ Memoized Stats Card Component
const StatsCard = memo(({ title, value, subtitle, icon: Icon, color, isMobile, theme }) => (
  <SummaryMetric sx={{
    boxShadow: isMobile ? 1 : 3, 
    border: `1px solid ${theme.palette[color].main}`,
    transition: 'all 0.3s ease',
    '&:hover': {
      transform: isMobile ? 'none' : 'translateY(-4px)',
      boxShadow: isMobile ? 2 : 6
    }
  }}>
    <CardContent sx={{ p: isMobile ? 2 : 3 }}>
      <Stack direction="row" alignItems="center" spacing={2}>
        <Avatar sx={{ 
          bgcolor: theme.palette[color].main,
          width: isMobile ? 40 : 48,
          height: isMobile ? 40 : 48
        }}>
          <Icon fontSize={isMobile ? "small" : "medium"} />
        </Avatar>
        <Box>
          <Typography variant={isMobile ? "body2" : "h6"} color="text.secondary">
            {title}
          </Typography>
          <Typography variant={isMobile ? "h5" : "h4"} fontWeight="bold">
            {value}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {subtitle}
          </Typography>
        </Box>
      </Stack>
    </CardContent>
  </SummaryMetric>
));

// ✅ Memoized Delete Confirmation Dialog
const DeleteConfirmationDialog = memo(({ 
  open, 
  onClose, 
  onConfirm, 
  assignmentToDelete, 
  isMobile 
}) => (
  <Dialog 
    open={open} 
    onClose={onClose}
    PaperProps={{ sx: { borderRadius: 2 } }}
    fullScreen={isMobile}
  >
    <DialogTitle sx={{ 
      bgcolor: 'error.main', 
      color: 'white',
      display: 'flex',
      alignItems: 'center',
      gap: 1,
      py: isMobile ? 2 : 3
    }}>
      <DeleteIcon />
      Confirm Assignment Deletion
    </DialogTitle>
    <DialogContent sx={{ p: isMobile ? 2 : 3 }}>
      <Typography gutterBottom>
        Are you sure you want to delete the assignment{' '}
        <strong>"{assignmentToDelete?.title}"</strong>?
      </Typography>
      <Alert severity="warning" sx={{ mt: 2 }}>
        This action cannot be undone. All student submissions and grades for this assignment will be permanently removed.
      </Alert>
    </DialogContent>
    <DialogActions sx={{ 
      p: isMobile ? 2 : 3, 
      gap: 1,
      flexDirection: isMobile ? 'column' : 'row'
    }}>
      <Button 
        onClick={onClose}
        variant="outlined"
        fullWidth={isMobile}
      >
        Cancel
      </Button>
      <Button 
        onClick={onConfirm} 
        variant="contained" 
        color="error"
        fullWidth={isMobile}
      >
        Delete Assignment
      </Button>
    </DialogActions>
  </Dialog>
));

function AssignmentsPage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  
  const [assignments, setAssignments] = useState([]);
  const [classes, setClasses] = useState([]);
  const [selectedClassId, setSelectedClassId] = useState('');
  const [role, setRole] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingInitial, setLoadingInitial] = useState(true);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [assignmentToDelete, setAssignmentToDelete] = useState(null);
  const [showAssignmentForm, setShowAssignmentForm] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  const [form, setForm] = useState({
    _id: '',
    title: '',
    class: '',
    dueDate: '',
    description: ''
  });

  // ✅ Memoized User Role Fetching
  const getUserRole = useCallback(() => {
    try {
      const storedUser = localStorage.getItem("user");
      if (!storedUser) return;
      const parsed = JSON.parse(storedUser);
      setRole(parsed.role);
    } catch (err) {
      console.error("Error reading user role:", err);
    }
  }, []);

  // ✅ Memoized Classes Fetching
  const fetchClasses = useCallback(async () => {
    try {
      const res = await api.get('/classes');
      const fetched = Array.isArray(res.data)
        ? res.data
        : Array.isArray(res.data.classes)
        ? res.data.classes
        : [];

      setClasses(fetched);

      if (fetched.length > 0) {
        setSelectedClassId(fetched[0]._id);
      }
    } catch (err) {
      console.error('Error fetching classes:', err);
    }
  }, []);

  // ✅ Memoized Assignments Fetching
  const fetchAllAssignmentsForAdmin = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/assignments/admin/all');
      setAssignments(Array.isArray(res.data?.items) ? res.data.items : []);
    } catch (err) {
      console.error('Error fetching admin assignments:', err);
      setAssignments([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchAssignmentsForClass = useCallback(async (classId) => {
    if (!classId) return;
    
    try {
      setLoading(true);
      const res = await api.get(`/assignments/${classId}`);
      if (Array.isArray(res.data?.assignments)) {
        setAssignments(res.data.assignments);
      } else if (Array.isArray(res.data)) {
        setAssignments(res.data);
      } else {
        setAssignments([]);
      }
    } catch (err) {
      console.error('Error fetching class assignments:', err.response?.data || err.message);
      setAssignments([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // ✅ Memoized Stats Calculations
  const { totalAssignments, upcomingAssignments, overdueAssignments } = useMemo(() => {
    const total = assignments.length;
    const upcoming = assignments.filter(a => {
      try {
        return new Date(a.dueDate) > new Date();
      } catch {
        return false;
      }
    }).length;
    const overdue = assignments.filter(a => {
      try {
        return new Date(a.dueDate) < new Date();
      } catch {
        return false;
      }
    }).length;

    return { totalAssignments: total, upcomingAssignments: upcoming, overdueAssignments: overdue };
  }, [assignments]);

  // ✅ Memoized Grouped Assignments
  const groupedByWeek = useMemo(() => {
    const grouped = {};
    assignments.forEach((a) => {
      try {
        const weekStart = format(startOfWeek(new Date(a.dueDate), { weekStartsOn: 1 }), 'yyyy-MM-dd');
        if (!grouped[weekStart]) grouped[weekStart] = [];
        grouped[weekStart].push(a);
      } catch (error) {
        console.error('Error processing assignment date:', error);
      }
    });
    return grouped;
  }, [assignments]);

  // ✅ Memoized Event Handlers
  const handleChange = useCallback((e) => 
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value })), []);

  const handleSubmit = useCallback(async () => {
    try {
      setLoading(true);
      const payload = {
        title: form.title,
        class: form.class || selectedClassId,
        dueDate: form.dueDate,
        description: form.description
      };

      if (isEditing) {
        await api.put(`/assignments/${form._id}`, payload);
      } else {
        await api.post('/assignments', payload);
      }

      setForm({ _id: '', title: '', class: '', dueDate: '', description: '' });
      setIsEditing(false);
      setShowAssignmentForm(false);

      // Refresh assignments after successful submission
      if (role === 'admin') {
        await fetchAllAssignmentsForAdmin();
      } else {
        await fetchAssignmentsForClass(selectedClassId);
      }
    } catch (err) {
      console.error('Error saving assignment:', err.response?.data || err.message);
    } finally {
      setLoading(false);
    }
  }, [form, isEditing, selectedClassId, role, fetchAllAssignmentsForAdmin, fetchAssignmentsForClass]);

  const handleEdit = useCallback((assignment) => {
    setForm({
      _id: assignment._id,
      title: assignment.title,
      class: assignment.class?._id || assignment.class,
      dueDate: assignment.dueDate?.split('T')[0],
      description: assignment.description
    });
    setIsEditing(true);
    setShowAssignmentForm(true);
  }, []);

  const handleDeleteClick = useCallback((assignment) => {
    setAssignmentToDelete(assignment);
    setShowConfirmDialog(true);
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (!assignmentToDelete) return;

    try {
      setLoading(true);
      await api.delete(`/assignments/${assignmentToDelete._id}`);
      
      if (role === 'admin') {
        await fetchAllAssignmentsForAdmin();
      } else {
        await fetchAssignmentsForClass(selectedClassId);
      }
    } catch (err) {
      console.error('Error deleting assignment:', err.response?.data || err.message);
    } finally {
      setLoading(false);
      setShowConfirmDialog(false);
      setAssignmentToDelete(null);
    }
  }, [assignmentToDelete, role, selectedClassId, fetchAllAssignmentsForAdmin, fetchAssignmentsForClass]);

  const resetForm = useCallback(() => {
    setForm({ _id: '', title: '', class: '', dueDate: '', description: '' });
    setIsEditing(false);
    setShowAssignmentForm(false);
  }, []);

  const handleClassChange = useCallback((e) => setSelectedClassId(e.target.value), []);

  // Initial setup - run only once
  useEffect(() => {
    const initializeData = async () => {
      try {
        setLoadingInitial(true);
        getUserRole();
        await fetchClasses();
      } catch (error) {
        console.error('Error initializing data:', error);
      } finally {
        setLoadingInitial(false);
      }
    };

    initializeData();
  }, [getUserRole, fetchClasses]);

  // Fetch assignments when role or selected class changes
  useEffect(() => {
    if (loadingInitial) return;

    if (role === 'admin') {
      fetchAllAssignmentsForAdmin();
      return;
    }

    if (selectedClassId && role === 'teacher') {
      fetchAssignmentsForClass(selectedClassId);
    }
  }, [selectedClassId, role, loadingInitial, fetchAllAssignmentsForAdmin, fetchAssignmentsForClass]);

  // ✅ Memoized Stats Cards Data
  const statsData = useMemo(() => [
    {
      title: 'Total Assignments',
      value: totalAssignments,
      subtitle: 'All assignments',
      icon: AssignmentIcon,
      color: 'primary'
    },
    {
      title: 'Upcoming',
      value: upcomingAssignments,
      subtitle: 'Future assignments',
      icon: CalendarToday,
      color: 'info'
    },
    {
      title: 'Overdue',
      value: overdueAssignments,
      subtitle: 'Past due assignments',
      icon: Visibility,
      color: 'warning'
    }
  ], [totalAssignments, upcomingAssignments, overdueAssignments]);

  if (loadingInitial) {
    return (
      <Container maxWidth="xl" sx={{ py: 4, px: isMobile ? 1 : 2 }}>
        <LoadingState />
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: isMobile ? 2 : 4, px: isMobile ? 1 : 2 }}>
      {/* Header */}
      <PageHeader title={<>Assignment Management</>} description={<>{role === 'admin' ? 'Manage all school assignments' : 'Create and manage class assignments'}</>}></PageHeader>
      <SummaryStrip container spacing={isMobile ? 2 : 3} sx={{ mb: isMobile ? 3 : 4 }}>
        {statsData.map((stat, index) => (
          <Grid size={{ xs: 6, sm: 4 }} key={stat.title}>
            <StatsCard 
              title={stat.title}
              value={stat.value}
              subtitle={stat.subtitle}
              icon={stat.icon}
              color={stat.color}
              isMobile={isMobile}
              theme={theme}
            />
          </Grid>
        ))}
      </SummaryStrip>

      {/* Stats Cards */}
      

      {/* Assignment Form Card */}
      <Card sx={{ 
        mb: isMobile ? 3 : 4, 
        boxShadow: isMobile ? 1 : 3, 
        border: `1px solid ${theme.palette.divider}` 
      }}>
        <CardContent sx={{ p: isMobile ? 2 : 4 }}>
          <Stack 
            direction={isMobile ? "column" : "row"} 
            alignItems={isMobile ? "flex-start" : "center"} 
            spacing={2} 
            sx={{ mb: 3 }}
          >
            <Stack direction="row" alignItems="center" spacing={2}>
              <Avatar sx={{ 
                bgcolor: theme.palette.primary.main,
                width: isMobile ? 32 : 40,
                height: isMobile ? 32 : 40
              }}>
                {isEditing ? <EditIcon fontSize={isMobile ? "small" : "medium"} /> : <AddIcon fontSize={isMobile ? "small" : "medium"} />}
              </Avatar>
              <Typography variant={isMobile ? "h6" : "h5"} fontWeight="600">
                {isEditing ? 'Edit Assignment' : 'Create New Assignment'}
              </Typography>
            </Stack>
            <Button
              variant="outlined"
              startIcon={showAssignmentForm ? <ExpandLess /> : <ExpandMore />}
              onClick={() => setShowAssignmentForm(!showAssignmentForm)}
              sx={{ ml: isMobile ? 0 : 'auto', mt: isMobile ? 1 : 0 }}
              fullWidth={isMobile}
              size={isMobile ? "medium" : "large"}
            >
              {showAssignmentForm ? 'Collapse Form' : 'Expand Form'}
            </Button>
          </Stack>

          <Collapse in={showAssignmentForm}>
            <Grid container spacing={isMobile ? 2 : 3}>
              <Grid size={{ xs: 12 }}>
                <TextField 
                  fullWidth 
                  label="Assignment Title" 
                  name="title" 
                  value={form.title} 
                  onChange={handleChange}
                  disabled={loading}
                  placeholder="Enter assignment title"
                  helperText="Give your assignment a clear and descriptive title"
                  size={isMobile ? "small" : "medium"}
                />
              </Grid>

              <Grid size={{ xs: 12 }}>
                <FormControl fullWidth disabled={loading || classes.length === 0} size={isMobile ? "small" : "medium"}>
                  <InputLabel>Class</InputLabel>
                  <Select
                    name="class"
                    value={form.class || selectedClassId}
                    onChange={handleChange}
                    label="Class"
                  >
                    {classes.map((c) => (
                      <MenuItem key={c._id} value={c._id}>
                        {c.displayName || c.name}
                      </MenuItem>
                    ))}
                  </Select>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                    Select the class for this assignment
                  </Typography>
                </FormControl>
              </Grid>

              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  label="Due Date"
                  name="dueDate"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  value={form.dueDate}
                  onChange={handleChange}
                  disabled={loading}
                  helperText="Set the deadline for this assignment"
                  size={isMobile ? "small" : "medium"}
                />
              </Grid>

              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  multiline
                  rows={isMobile ? 3 : 4}
                  label="Assignment Description"
                  name="description"
                  value={form.description}
                  onChange={handleChange}
                  disabled={loading}
                  placeholder="Provide detailed instructions for the assignment..."
                  helperText="Include all necessary instructions, requirements, and submission guidelines"
                  size={isMobile ? "small" : "medium"}
                />
              </Grid>

              <Grid size={{ xs: 12 }}>
                <ActionToolbar 
                  direction={isMobile ? "column" : "row"} 
                  spacing={2} 
                  justifyContent="flex-end"
                  alignItems={isMobile ? "stretch" : "center"}
                >
                  {isEditing && (
                    <Button onClick={resetForm} variant="outlined" disabled={loading} fullWidth={isMobile}>
                      Cancel
                    </Button>
                  )}
                  <Button 
                    onClick={handleSubmit} 
                    variant="contained" 
                    size={isMobile ? "medium" : "large"} 
                    disabled={loading}
                    startIcon={loading ? <CircularProgress size={20} /> : null}
                    fullWidth={isMobile}
                  >
                    {loading ? 'Saving...' : isEditing ? 'Update Assignment' : 'Create Assignment'}
                  </Button>
                </ActionToolbar>
              </Grid>
            </Grid>
          </Collapse>
        </CardContent>
      </Card>

      {/* Class Selector for Teachers */}
      {role !== 'admin' && (
        <FilterBar sx={{
          mb: isMobile ? 3 : 4, 
          boxShadow: isMobile ? 1 : 3, 
          border: `1px solid ${theme.palette.divider}` 
        }}>
          <CardContent sx={{ p: isMobile ? 2 : 3 }}>
            <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
              <Avatar sx={{ 
                bgcolor: theme.palette.info.main,
                width: isMobile ? 32 : 40,
                height: isMobile ? 32 : 40
              }}>
                <Class fontSize={isMobile ? "small" : "medium"} />
              </Avatar>
              <Typography variant={isMobile ? "h6" : "h6"} fontWeight="600">
                Select Class to View Assignments
              </Typography>
            </Stack>
            <FormControl fullWidth size={isMobile ? "small" : "medium"}>
              <InputLabel>Class</InputLabel>
              <Select
                value={selectedClassId}
                label="Class"
                onChange={handleClassChange}
              >
                {classes.map((c) => (
                  <MenuItem key={c._id} value={c._id}>
                    {c.displayName || c.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </CardContent>
        </FilterBar>
      )}

      {/* Assignments List */}
      <Card sx={{ 
        boxShadow: isMobile ? 1 : 3, 
        border: `1px solid ${theme.palette.divider}` 
      }}>
        <CardContent sx={{ p: 0 }}>
          <Box sx={{ 
            p: isMobile ? 2 : 3, 
            borderBottom: `1px solid ${theme.palette.divider}` 
          }}>
            <Stack 
              direction={isMobile ? "column" : "row"} 
              justifyContent="space-between" 
              alignItems={isMobile ? "flex-start" : "center"}
              spacing={isMobile ? 1 : 0}
            >
              <Typography variant={isMobile ? "h6" : "h6"} fontWeight="600">
                {role === 'admin' ? 'All School Assignments' : 'Class Assignments'} ({assignments.length})
              </Typography>
              {assignments.length > 0 && (
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: isMobile ? 1 : 0 }}>
                  <Chip 
                    label={`${totalAssignments} total`} 
                    color="primary"
                    variant="outlined"
                    size={isMobile ? "small" : "medium"}
                  />
                  <Chip 
                    label={`${upcomingAssignments} upcoming`}
                    color="info"
                    variant="outlined"
                    size={isMobile ? "small" : "medium"}
                  />
                </Stack>
              )}
            </Stack>
          </Box>

          {loading ? (
            <LoadingState />
          ) : assignments.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 6, px: 2 }}>
              <AssignmentIcon sx={{ 
                fontSize: isMobile ? 48 : 64, 
                color: 'text.secondary', 
                opacity: 0.5, 
                mb: 2 
              }} />
              <Typography variant={isMobile ? "h6" : "h6"} color="text.secondary" gutterBottom>
                No assignments found
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {role === 'admin' 
                  ? 'No assignments have been created yet' 
                  : `No assignments found for selected class`
                }
              </Typography>
            </Box>
          ) : (
            <Box sx={{ 
              width: '100%', 
              overflow: 'auto',
              maxHeight: isTablet ? '60vh' : 'none'
            }}>
              <Box sx={{ p: isMobile ? 1 : 3 }}>
                {Object.keys(groupedByWeek).length === 0 ? (
                  <Typography>No assignments.</Typography>
                ) : (
                  Object.keys(groupedByWeek).sort().map((week) => (
                    <WeekGroup
                      key={week}
                      week={week}
                      assignments={groupedByWeek[week]}
                      isMobile={isMobile}
                      isTablet={isTablet}
                      onEdit={handleEdit}
                      onDelete={handleDeleteClick}
                      theme={theme}
                    />
                  ))
                )}
              </Box>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmationDialog
        open={showConfirmDialog}
        onClose={() => setShowConfirmDialog(false)}
        onConfirm={handleDeleteConfirm}
        assignmentToDelete={assignmentToDelete}
        isMobile={isMobile}
      />
    </Container>
  );
}

// ✅ Export with memo
export default memo(AssignmentsPage);